// HomePeople.js
import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Box, styled } from "@mui/material";
import Header from "../componentsPeopletech/Header";
import BarraApp from "../componentsPeopletech/Barraaps"; // Asegúrate de que sea el nombre correcto
import ServicesCircles from "../componentsPeopletech/ServicesCircles";
import Footer from "../componentsPeopletech/Footer/Footer";
import ContactSection from "../componentsPeopletech/ContactSection";

const HomePeople = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const baseUrl = 'https://www.peoplefortech.com';

  const BackgroundVideoContainer = styled(Box)({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      zIndex: 1,
    },
  });

  const Video = styled('video')({
    position: 'absolute',
    top: '50%',
    left: '50%',
    minWidth: '100%',
    minHeight: '100%',
    width: 'auto',
    height: 'auto',
    transform: 'translate(-50%, -50%)',
    filter: 'blur(5px)',
    objectFit: 'cover',
    zIndex: 0,
    opacity: 1,
  });

  const ContentWrapper = styled(Box)({
    position: 'relative',
    zIndex: 1,
    backgroundColor: 'transparent',
  });

  return (
    <>
      <Helmet>
        {/* Meta tags básicos */}
        <title>{t('seo.homeTitle')}</title>
        <meta name="description" content={t('seo.homeDescription')} />
        <meta name="keywords" content={t('seo.homeKeywords')} />
        <meta name="author" content="PeopleForTech" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <link rel="canonical" href={`${baseUrl}/${currentLang === 'en' ? '' : currentLang + '/'}`} />
        <html lang={currentLang} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/${currentLang === 'en' ? '' : currentLang + '/'}`} />
        <meta property="og:title" content={t('seo.homeTitle')} />
        <meta property="og:description" content={t('seo.homeDescription')} />
        <meta property="og:image" content={`${baseUrl}/og-image.jpg`} />
        <meta property="og:locale" content={currentLang} />
        <meta property="og:locale:alternate" content="en" />
        <meta property="og:locale:alternate" content="es" />
        <meta property="og:locale:alternate" content="pt" />
        <meta property="og:locale:alternate" content="fr" />
        <meta property="og:locale:alternate" content="de" />
        <meta property="og:locale:alternate" content="zh" />
        <meta property="og:locale:alternate" content="ar" />
        <meta property="og:locale:alternate" content="hi" />
        <meta property="og:locale:alternate" content="ru" />
        <meta property="og:locale:alternate" content="ja" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${baseUrl}/${currentLang === 'en' ? '' : currentLang + '/'}`} />
        <meta property="twitter:title" content={t('seo.homeTitle')} />
        <meta property="twitter:description" content={t('seo.homeDescription')} />
        <meta property="twitter:image" content={`${baseUrl}/twitter-image.jpg`} />
        
        {/* Hreflang tags para multiidioma */}
        <link rel="alternate" hreflang="en" href={`${baseUrl}/`} />
        <link rel="alternate" hreflang="es" href={`${baseUrl}/es`} />
        <link rel="alternate" hreflang="pt" href={`${baseUrl}/pt`} />
        <link rel="alternate" hreflang="fr" href={`${baseUrl}/fr`} />
        <link rel="alternate" hreflang="de" href={`${baseUrl}/de`} />
        <link rel="alternate" hreflang="zh" href={`${baseUrl}/zh`} />
        <link rel="alternate" hreflang="ar" href={`${baseUrl}/ar`} />
        <link rel="alternate" hreflang="hi" href={`${baseUrl}/hi`} />
        <link rel="alternate" hreflang="ru" href={`${baseUrl}/ru`} />
        <link rel="alternate" hreflang="ja" href={`${baseUrl}/ja`} />
        <link rel="alternate" hreflang="x-default" href={`${baseUrl}/`} />
        
        {/* Schema.org JSON-LD para SEO de IA - Mejorado con variantes de servicios */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": `${baseUrl}/#organization`,
                "name": "PeopleForTech",
                "alternateName": ["PXT", "People For Tech"],
                "url": baseUrl,
                "logo": `${baseUrl}/logo.png`,
                "description": t('seo.homeDescription'),
                "foundingDate": "2024",
                "knowsAbout": [
                  "Inteligencia Artificial",
                  "Artificial Intelligence",
                  "AI Software Development",
                  "Desarrollo de Software de IA",
                  "Consultoría en IA",
                  "AI Consulting",
                  "SEO de IA",
                  "AI SEO",
                  "Machine Learning",
                  "Aprendizaje Automático",
                  "Aplicaciones Móviles con IA",
                  "Mobile Apps with AI",
                  "Desarrollo Web con IA",
                  "Web Development with AI",
                  "ChatGPT",
                  "Gemini",
                  "Schema.org",
                  "JSON-LD"
                ],
                "sameAs": [
                  "https://www.linkedin.com/in/people-for-tech",
                  "https://www.tiktok.com/@peoplefortech"
                ],
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "Customer Service",
                  "email": "contact@peoplefortech.com",
                  "availableLanguage": ["en", "es", "pt", "fr", "de", "zh", "ar", "hi", "ru", "ja"]
                },
                "areaServed": {
                  "@type": "Place",
                  "name": "Worldwide"
                }
              },
              {
                "@type": "WebSite",
                "@id": `${baseUrl}/#website`,
                "url": baseUrl,
                "name": "PeopleForTech - Desarrollo de Software de IA y Consultoría",
                "alternateName": "PXT",
                "description": t('seo.homeDescription'),
                "publisher": {
                  "@id": `${baseUrl}/#organization`
                },
                "inLanguage": currentLang,
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": `${baseUrl}/search?q={search_term_string}`,
                  "query-input": "required name=search_term_string"
                }
              },
              {
                "@type": "Service",
                "name": "Consultoría en Inteligencia Artificial",
                "alternateName": [
                  "Consultoría IA",
                  "AI Consulting",
                  "Consultoría de IA",
                  "Asesoría en Inteligencia Artificial",
                  "Integración de IA en Negocios"
                ],
                "description": t('services.consultory.description'),
                "provider": {
                  "@id": `${baseUrl}/#organization`
                },
                "serviceType": ["AI Consultory", "AI Consulting", "Inteligencia Artificial", "Machine Learning Consulting"],
                "areaServed": "Worldwide",
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Servicios de Consultoría en IA",
                  "itemListElement": [
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Análisis y Estrategia de IA"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Integración de IA en Procesos Empresariales"
                      }
                    }
                  ]
                }
              },
              {
                "@type": "Service",
                "name": "Desarrollo de Software de Inteligencia Artificial",
                "alternateName": [
                  "Desarrollo de Software de IA",
                  "AI Software Development",
                  "Desarrollo de Aplicaciones con IA",
                  "Desarrollo de Apps de IA",
                  "Software de Inteligencia Artificial",
                  "Aplicaciones Móviles con IA",
                  "Desarrollo Web con IA"
                ],
                "description": t('services.development.description'),
                "provider": {
                  "@id": `${baseUrl}/#organization`
                },
                "serviceType": [
                  "AI Software Development",
                  "Desarrollo de Software de IA",
                  "Mobile App Development with AI",
                  "Desarrollo de Aplicaciones Móviles con IA",
                  "Web Development with AI",
                  "Desarrollo Web con IA",
                  "iOS App Development",
                  "Android App Development"
                ],
                "areaServed": "Worldwide",
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Servicios de Desarrollo de Software de IA",
                  "itemListElement": [
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Desarrollo de Aplicaciones Móviles iOS con IA"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Desarrollo de Aplicaciones Móviles Android con IA"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Desarrollo Web Responsive con IA"
                      }
                    }
                  ]
                }
              },
              {
                "@type": "Service",
                "name": "SEO de Inteligencia Artificial",
                "alternateName": [
                  "SEO de IA",
                  "AI SEO",
                  "Posicionamiento en Asistentes de IA",
                  "SEO para ChatGPT",
                  "SEO para Gemini",
                  "Schema.org Implementation",
                  "JSON-LD Implementation"
                ],
                "description": t('services.seo.description'),
                "provider": {
                  "@id": `${baseUrl}/#organization`
                },
                "serviceType": [
                  "AI SEO",
                  "SEO de IA",
                  "ChatGPT SEO",
                  "Gemini SEO",
                  "Schema.org Implementation",
                  "JSON-LD SEO"
                ],
                "areaServed": "Worldwide",
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Servicios de SEO de IA",
                  "itemListElement": [
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Posicionamiento en ChatGPT"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Posicionamiento en Gemini"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Implementación de Schema.org/JSON-LD"
                      }
                    }
                  ]
                }
              },
              {
                "@type": "SoftwareApplication",
                "name": "PeopleForTech Platform",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": ["iOS", "Android", "Web"],
                "offers": {
                  "@type": "Offer",
                  "price": "0",
                  "priceCurrency": "USD"
                },
                "provider": {
                  "@id": `${baseUrl}/#organization`
                },
                "description": "Plataforma de servicios de inteligencia artificial para empresas"
              }
            ]
          })}
        </script>
      </Helmet>
      <BackgroundVideoContainer>
        <Video
          autoPlay
          loop
          muted
          playsInline
          src="/18192389-hd_1920_1080_60fps.mp4"
        >
          Tu navegador no soporta la reproducción de videos.
        </Video>
      </BackgroundVideoContainer>
      <ContentWrapper>
        <BarraApp />
        <Header /> {/* Componente de encabezado */}
        <ServicesCircles /> {/* Sección de servicios con círculos negros */}
        <ContactSection /> {/* Sección de contacto */}
        <Footer />
      </ContentWrapper>
    </>
  );
};

export default HomePeople;
